import React, { useMemo, useRef, useState } from 'react';
import {
  Create,
  SaveButton,
  SimpleForm,
  Toolbar,
  required,
  useNotify,
  useRedirect,
} from 'react-admin';
import { Button, Stack, Typography } from '@mui/material';
import CompanyInput from 'components/CompanyInput';
import useApi from 'hooks/useApi';

const ReverseInvoiceFilesCreateToolbar = ({ isSubmitting }) => (
  <Toolbar>
    <SaveButton label={isSubmitting ? 'Uploading...' : 'Save'} disabled={isSubmitting} />
  </Toolbar>
);

const ReverseInvoiceFilesCreate = () => {
  const api = useApi();
  const notify = useNotify();
  const redirect = useRedirect();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedFileLabel = useMemo(() => file?.name || 'No file selected', [file]);

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [nextFile] = event.target.files || [];
    setFile(nextFile || null);
  };

  const handleSubmit = async (values) => {
    if (isSubmitting) {
      return;
    }

    if (!file) {
      notify('File is required', { type: 'warning' });
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('reverse_invoice_file[company_id]', String(values.company_id));
      formData.append('reverse_invoice_file[file]', file);

      await api({
        url: '/v3/admin/reverse_invoice_files',
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      notify('Reverse invoice file uploaded', { type: 'success' });
      redirect('list', 'reverseInvoiceFile');
    } catch (error: any) {
      notify(error?.response?.data?.message ?? error?.response?.data?._error ?? error?.message, {
        type: 'error',
        autoHideDuration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create>
      <SimpleForm
        onSubmit={handleSubmit}
        toolbar={<ReverseInvoiceFilesCreateToolbar isSubmitting={isSubmitting} />}
      >
        <CompanyInput source="company_id" validate={required()} fullWidth />
        <input hidden ref={fileInputRef} type="file" onChange={onPickFile} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            type="button"
            variant="outlined"
            disabled={isSubmitting}
            onClick={() => fileInputRef.current?.click()}
          >
            Select File
          </Button>
          <Typography variant="body2">{selectedFileLabel}</Typography>
        </Stack>
      </SimpleForm>
    </Create>
  );
};

export default ReverseInvoiceFilesCreate;
